/**
 * The replica's `.page-head` for the desktop rail layout — drawn on HOME only,
 * where it carries the period/account context the phone header used to hold
 * (the header is gone at `lg`+). Every other screen already titles itself
 * through `<PageHeader>` or its own toolbar, exactly the reasoning in
 * `WalletHeader.tsx`, so this renders nothing there.
 */
import { useLocation } from "react-router-dom";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useAdminScope } from "@/lib/hooks/use-admin-scope";
import { accountLabel, useWalletShell } from "./context";
import { HOME_HREF } from "./nav";

const iconBtn =
  "inline-flex h-7 w-7 items-center justify-center rounded-md text-[var(--cp-ink-3)] transition-colors hover:bg-[var(--cp-surface-2)] hover:text-[var(--cp-ink)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--cp-accent)]";

export function DesktopPageHead({ onOpenContext }: { onOpenContext(): void }) {
  const { pathname } = useLocation();
  const { bounds, shiftPeriod, isCurrentPeriod, resetPeriod, accountId, accounts } =
    useWalletShell();
  const { viewAll } = useAdminScope();

  if (pathname !== HOME_HREF && pathname !== "/") return null;

  const selected = accountId ? accounts.find((a) => a.id === accountId) : null;
  const scope = selected ? accountLabel(selected) : viewAll ? "family" : "your data";

  return (
    <div className="wd-page-head hidden lg:flex">
      <h1>Home</h1>
      <span className="sub flex items-center gap-1">
        <button
          type="button"
          onClick={() => shiftPeriod(-1)}
          aria-label="Previous period (desktop)"
          className={iconBtn}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={onOpenContext}
          aria-haspopup="dialog"
          className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-semibold text-[var(--cp-ink-2)] hover:bg-[var(--cp-surface-2)]"
        >
          {bounds.label} · {scope}
          <ChevronDown className="h-3.5 w-3.5 text-[var(--cp-ink-3)]" />
        </button>
        <button
          type="button"
          onClick={() => shiftPeriod(1)}
          aria-label="Next period (desktop)"
          className={iconBtn}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
        {!isCurrentPeriod && (
          <button
            type="button"
            onClick={resetPeriod}
            className="ml-1 rounded-md px-2 py-0.5 text-xs font-semibold text-[var(--cp-accent-ink)] hover:bg-[var(--cp-surface-2)]"
          >
            Today
          </button>
        )}
      </span>
    </div>
  );
}
